import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Row, Col, Card, message, Radio, Divider } from 'antd';
import { studentAPI, schoolAPI, classAPI } from '../../services/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const { Option } = Select;
const { TextArea } = Input;

const getScopedSchoolId = (user) => {
  const schoolId = user?.schoolId ?? user?.school_id ?? user?.schoolIds?.[0] ?? user?.school_ids?.[0];
  return schoolId === undefined || schoolId === null || schoolId === '' ? null : String(schoolId);
};

const resolveScopedSchoolId = (user, availableSchools) => {
  const scopedSchoolId = getScopedSchoolId(user);
  const scopedSchool = availableSchools.find((school) => String(school.id) === scopedSchoolId);
  if (scopedSchool) return scopedSchoolId;

  // 当旧默认学校已不在当前可访问范围内时，才按实际班级归属回退。
  // “待绑定学校”本身也可以是仍在使用的有效学校，不能按名称排除。
  const classSchoolIds = (user?.classes || [])
    .map((item) => item?.schoolId ?? item?.school_id)
    .filter((value) => value !== undefined && value !== null)
    .map(String);
  const classSchool = availableSchools.find((school) => classSchoolIds.includes(String(school.id)));
  if (classSchool) return String(classSchool.id);

  return availableSchools[0] ? String(availableSchools[0].id) : scopedSchoolId;
};

const StudentForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  // 获取学校列表
  const fetchSchools = async () => {
    console.log('StudentForm: Attempting to fetch schools...');
    try {
      const response = await schoolAPI.getAll();
      console.log('StudentForm: Schools fetched successfully:', response.data.data);
      const availableSchools = response.data.data || [];
      setSchools(availableSchools);

      // 学校管理员和教师固定到自己所属学校
      const scopedSchoolId = resolveScopedSchoolId(currentUser, availableSchools);
      const defaultSchoolId = scopedSchoolId || (availableSchools.length === 1 ? String(availableSchools[0].id) : null);
      if (currentUser && ['school_admin', 'teacher'].includes(currentUser.role) && defaultSchoolId) {
        console.log('StudentForm: scoped user detected, setting school to:', defaultSchoolId);
        setSelectedSchool(defaultSchoolId);
        form.setFieldsValue({ school_id: defaultSchoolId });
        fetchClasses(defaultSchoolId);
      }
    } catch (error) {
      console.error('获取学校列表失败:', error);
      message.error('获取学校列表失败');
    }
  };

  // 获取班级列表
  const fetchClasses = async (schoolId) => {
    if (!schoolId) {
      setClasses([]);
      return;
    }

    try {
      const response = await classAPI.getAll({ schoolId, pageSize: 200 });
      // 按学校在服务端筛选，避免一次性拉取全平台班级。
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('获取班级列表失败:', error);
      message.error('获取班级列表失败');
    }
  };

  // 获取学生详情
  const fetchStudent = async () => {
    setLoading(true);
    try {
      const response = await studentAPI.getById(id);
      const student = response.data.data;
      
      // 处理学校和班级
      if (student.school_id) {
        setSelectedSchool(student.school_id);
        fetchClasses(student.school_id);
      }
      
      // 设置表单数据
      form.setFieldsValue(student);
    } catch (error) {
      console.error('获取学生详情失败:', error);
      message.error('获取学生详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    if (currentUser) {
      fetchSchools();
      if (isEditing) {
        fetchStudent();
      }
    }
  }, [isEditing, id, currentUser]);

  // 提交表单
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const data = {
        ...values,
        school_id: values.school_id ?? selectedSchool ?? getScopedSchoolId(currentUser)
      };
      if (isEditing) {
        await studentAPI.update(id, data);
        message.success('学生信息更新成功');
      } else {
        await studentAPI.create(data);
        message.success('学生添加成功');
      }
      navigate('/students');
    } catch (error) {
      console.error('提交学生信息失败:', error);
      message.error('提交学生信息失败');
    } finally {
      setLoading(false);
    }
  };

  // 学校变化时获取相应班级
  const handleSchoolChange = (value) => {
    setSelectedSchool(value);
    form.setFieldsValue({ class_id: undefined });
    fetchClasses(value);
  };

  return (
    <div>
      <Card title={isEditing ? '编辑学生' : '添加学生'}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            gender: '男',
            is_boarding: '否',
            is_migrant_worker: '否',
            is_left_behind: '否',
            is_disabled: '否'
          }}
        >
          <h3>基本信息</h3>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="student_name"
                label="学生姓名"
                rules={[{ required: true, message: '请输入学生姓名' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="student_id"
                label="全国学籍号"
                rules={[{ required: true, message: '请输入学籍号' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="gender"
                label="性别"
                rules={[{ required: true, message: '请选择性别' }]}
              >
                <Radio.Group>
                  <Radio value="男">男</Radio>
                  <Radio value="女">女</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="school_id"
                label="所属学校"
                rules={[{ required: true, message: '请选择所属学校' }]}
              >
                <Select 
                  placeholder="选择学校" 
                  onChange={handleSchoolChange}
                  disabled={currentUser?.role === 'school_admin' && schools.length === 1}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {schools.map(school => (
                    <Option key={school.id} value={school.id}>{school.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="grade"
                label="年级"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="class_id"
                label="班级"
              >
                <Select 
                  placeholder="选择班级" 
                  disabled={!selectedSchool}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>
                      {cls.grade ? `${cls.grade} ${cls.name}` : cls.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="id_card"
                label="身份证件号"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="ethnicity"
                label="民族"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="birth_date"
                label="出生日期"
              >
                <Input placeholder="格式: YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <h3>家庭信息</h3>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="parent1_name"
                label="家长姓名1"
                rules={[{ required: true, message: '请输入家长姓名' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="parent1_phone"
                label="家长联系电话1"
                rules={[{ required: true, message: '请输入家长联系电话' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="family_relation"
                label="家庭成员关系"
              >
                <Input placeholder="例如: 父亲、母亲" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="parent2_name"
                label="家长姓名2"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="parent2_phone"
                label="家长联系电话2"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="parent_work_unit"
                label="家庭成员工作单位"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="parent_id_card"
                label="家庭成员身份证件号"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_guardian"
                label="家庭成员是否监护人"
              >
                <Select placeholder="请选择" allowClear>
                  <Option value="是">是</Option>
                  <Option value="否">否</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <h3>住址信息</h3>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="current_address"
                label="现居住详细地址"
              >
                <TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="residence_address"
                label="户口所在地具体地址"
              >
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />
          <h3>其他信息</h3>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="is_boarding"
                label="是否寄宿生"
              >
                <Select placeholder="请选择" allowClear>
                  <Option value="是">是</Option>
                  <Option value="否">否</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_migrant_worker"
                label="是否进城务工人员随迁子女"
              >
                <Select placeholder="请选择" allowClear>
                  <Option value="是">是</Option>
                  <Option value="否">否</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_left_behind"
                label="是否农村留守儿童"
              >
                <Select placeholder="请选择" allowClear>
                  <Option value="是">是</Option>
                  <Option value="否">否</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="pinyin_name"
                label="姓名拼音"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="former_name"
                label="曾用名"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="political_status"
                label="政治面貌"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="nationality"
                label="国籍/地区"
              >
                <Input placeholder="默认中国" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="id_card_type"
                label="身份证件类型"
              >
                <Input placeholder="例如: 居民身份证" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_disabled"
                label="是否残疾人"
              >
                <Select placeholder="请选择" allowClear>
                  <Option value="是">是</Option>
                  <Option value="否">否</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEditing ? '更新' : '添加'}
            </Button>
            <Button 
              style={{ marginLeft: 8 }} 
              onClick={() => navigate('/students')}
            >
              返回
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default StudentForm; 
